// sbu-load-more — one continuous-list loader for every paginated product list
// (snippets/sbu-load-more.liquid; 2026-10-06). See that snippet's doc.
//
// WHERE THE PAGE SCROLLS (root cause, 2026-10-06). Horizon sets html and body
// to overflow:hidden; the page scrolls inside div.page-wrapper (overflow-y:auto,
// layout/theme.liquid; snippets/sbu-tokens.liquid notes it too). So `scroll`
// never fires on window for a reader using a wheel, trackpad or keyboard, and
// an IntersectionObserver rooted at the viewport saw every card as off-screen.
// The first version listened on window only and was "verified" with
// window.scrollTo, which scrolls the hidden root and fired the event: the
// test did what no reader does. Now:
//   - one capturing `scroll` listener on document hears ANY scroller
//     (page-wrapper, the root, anything Horizon adds later);
//   - the last card is measured against the viewport, which is what the
//     reader sees whichever box scrolls;
//   - a check also runs on load, after every append and on a slow timer while
//     pages remain, so a page that does not fill the screen keeps loading and
//     a missed event cannot strand the list.
const AHEAD = 1200;
const TICK_MS = 700;

const keyOf = (li) => {
  const a = li.querySelector('a[href*="/products/"]') || li.querySelector('a[href]');
  const href = a ? a.getAttribute('href') : '';
  const m = href.match(/\/products\/([^/?#]+)/);
  return m ? 'p:' + m[1] : href ? 'h:' + href.split('?')[0] : '';
};

class SbuLoadMore extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.status = this.querySelector('[data-sbu-load-status]');
    this.sels = (this.dataset.lists || '').split(',').map((s) => s.trim()).filter(Boolean);
    this.lists = this.sels.map((s) => document.querySelector(s));
    if (!this.dataset.next || !this.lists.some(Boolean)) return;
    this.setAttribute('data-live', '');
    this.seen = new Set();
    for (const list of this.lists) if (list) for (const li of list.children) this.seen.add(keyOf(li));
    const opts = { passive: true, signal: this.abort.signal };
    let queued = false;
    this.onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; this.check(); });
    };
    document.addEventListener('scroll', this.onScroll, { ...opts, capture: true });
    addEventListener('resize', this.onScroll, opts);
    this.timer = setInterval(() => this.check(), TICK_MS);
    this.check();
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearInterval(this.timer);
  }

  check() {
    if (!this.dataset.next) return;
    const first = this.lists.find(Boolean);
    const last = first && first.lastElementChild;
    if (last && last.getBoundingClientRect().top < innerHeight + AHEAD) this.more();
  }

  stop() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    removeEventListener('resize', this.onScroll);
    clearInterval(this.timer);
  }

  say(text) { if (this.status) this.status.textContent = text; }

  count() {
    const first = this.lists.find(Boolean);
    return first ? first.children.length : 0;
  }

  async more() {
    if (this.busy || !this.dataset.next) return;
    this.busy = true;
    this.say('Loading more…');
    try {
      const url = new URL(this.dataset.next, location.origin);
      url.searchParams.set('sections', this.dataset.sectionId);
      // No Accept: application/json header: with it Shopify can answer the
      // resource's own JSON instead of the sections payload.
      const res = await fetch(url, { signal: this.abort.signal });
      if (!res.ok) throw new Error(String(res.status));
      const html = (await res.json())[this.dataset.sectionId];
      const doc = new DOMParser().parseFromString(html || '', 'text/html');
      this.sels.forEach((sel, i) => {
        const here = this.lists[i];
        const there = doc.querySelector(sel);
        if (!here || !there) return;
        const frag = document.createDocumentFragment();
        for (const li of [...there.children]) {
          const k = keyOf(li);
          if (k && this.seen.has(k)) continue;
          if (k) this.seen.add(k);
          frag.append(document.importNode(li, true));
        }
        here.append(frag);
      });
      const next = doc.querySelector('sbu-load-more')?.dataset.next;
      if (next && next !== this.dataset.next) this.dataset.next = next;
      else delete this.dataset.next;
      const noun = this.dataset.noun || 'books';
      const shown = this.count();
      const total = Number(this.dataset.total) || 0;
      this.say(this.dataset.next
        ? `Showing ${shown}${total ? ` of ${total}` : ''} ${noun}`
        : `All ${shown} ${noun} shown`);
    } catch (e) {
      if (e.name === 'AbortError') return;
      this.removeAttribute('data-live');
      this.stop();
      this.say('');
      return;
    } finally {
      this.busy = false;
    }
    if (this.dataset.next) this.check();
    else this.stop();
  }
}

if (!customElements.get('sbu-load-more')) customElements.define('sbu-load-more', SbuLoadMore);
